<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Pause, Play, RotateCcw } from '@lucide/vue'
import type { ParsedDocument } from '../app/types'

const props = defineProps<{
  document: ParsedDocument
}>()

const containerRef = ref<HTMLDivElement>()
const loading = ref(true)
const error = ref('')
const isPlaying = ref(true)
let animation: {
  play: () => void
  pause: () => void
  destroy: () => void
  goToAndPlay: (value: number, isFrame?: boolean) => void
} | undefined
let loadSequence = 0

const metadata = computed(() => props.document.jsonAnimation)
const durationSeconds = computed(() => {
  const meta = metadata.value
  if (!meta || meta.frameRate <= 0) {
    return 0
  }

  return Math.max(0, (meta.outPoint - meta.inPoint) / meta.frameRate)
})

onMounted(loadAnimation)
watch(() => props.document.rawText, loadAnimation)

onBeforeUnmount(() => {
  loadSequence += 1
  animation?.destroy()
})

async function loadAnimation() {
  const sequence = ++loadSequence
  const source = props.document.rawText
  animation?.destroy()
  animation = undefined
  loading.value = true
  error.value = ''

  await nextTick()

  const container = containerRef.value
  if (!container || sequence !== loadSequence) {
    return
  }

  try {
    const lottie = await import('lottie-web/build/player/lottie_light')
    if (sequence !== loadSequence) {
      return
    }

    const animationData = JSON.parse(source)
    const nextAnimation = lottie.default.loadAnimation({
      container,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData,
    })

    if (sequence !== loadSequence) {
      nextAnimation.destroy()
      return
    }

    animation = nextAnimation
    isPlaying.value = true
  } catch (exception) {
    if (sequence === loadSequence) {
      error.value = exception instanceof Error ? exception.message : '动画加载失败'
    }
  } finally {
    if (sequence === loadSequence) {
      loading.value = false
    }
  }
}

function togglePlayback() {
  if (!animation) {
    return
  }

  if (isPlaying.value) {
    animation.pause()
    isPlaying.value = false
  } else {
    animation.play()
    isPlaying.value = true
  }
}

function restart() {
  animation?.goToAndPlay(0, true)
  isPlaying.value = true
}
</script>

<template>
  <section class="lottie-preview" aria-label="JSON 动画预览">
    <header class="lottie-head">
      <div>
        <p class="eyebrow">Lottie 动画</p>
        <h2>{{ metadata?.name ?? document.title }}</h2>
        <small v-if="metadata">
          {{ metadata.width }} × {{ metadata.height }} · {{ metadata.frameRate }} fps · {{ durationSeconds.toFixed(1) }}s
        </small>
      </div>
      <div class="lottie-actions">
        <button class="icon-button" type="button" :aria-label="isPlaying ? '暂停动画' : '播放动画'" @click="togglePlayback">
          <Pause v-if="isPlaying" :size="18" aria-hidden="true" />
          <Play v-else :size="18" aria-hidden="true" />
        </button>
        <button class="icon-button" type="button" aria-label="重新播放动画" @click="restart">
          <RotateCcw :size="18" aria-hidden="true" />
        </button>
      </div>
    </header>

    <div class="lottie-stage">
      <div ref="containerRef" class="lottie-canvas" />
      <p v-if="loading" class="lottie-state">正在加载动画...</p>
      <p v-if="error" class="lottie-state error">动画预览失败：{{ error }}</p>
    </div>
  </section>
</template>
